import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { fetchFileAsArrayBuffer } from '@/utils/fileUtils';

const ROWS_PER_PAGE = 50;

export default function ExcelViewer({ open, onOpenChange, fileKey, fileName, bucket }) {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && fileKey) {
      loadExcelData();
    }
  }, [open, fileKey]);

  const loadExcelData = async () => {
    setLoading(true);
    try {
      // Use utility function to fetch file
      const arrayBuffer = await fetchFileAsArrayBuffer(fileKey, bucket);

      // Parse the Excel file
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

      // Get all sheet names
      const sheetNames = workbook.SheetNames;
      setSheets(sheetNames);

      // Load the first sheet by default
      if (sheetNames.length > 0) {
        setSelectedSheet(sheetNames[0]);
        loadSheetData(workbook, sheetNames[0]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading Excel file:', error);
      toast.error(`Failed to load Excel file: ${error.message}`);
      setLoading(false);
    }
  };

  const loadSheetData = (workbook, sheetName) => {
    const worksheet = workbook.Sheets[sheetName];

    // Convert sheet to JSON
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (jsonData.length > 0) {
      // First row as headers
      const headers = jsonData[0].map((h, idx) => h || `Column ${idx + 1}`);
      setColumns(headers);

      // Rest as data rows
      const rows = jsonData.slice(1).map((row) => {
        const rowObj = {};
        headers.forEach((header, idx) => {
          const val = row[idx];
          rowObj[header] = val instanceof Date
            ? val.toLocaleDateString('en-GB')
            : val != null ? val : '';
        });
        return rowObj;
      });

      setData(rows);
    } else {
      setColumns([]);
      setData([]);
    }

    setCurrentPage(1);
  };

  const handleSheetChange = (sheetName) => {
    setSelectedSheet(sheetName);
    setLoading(true);

    // Re-load the file to get the workbook
    fetchFileAsArrayBuffer(fileKey, bucket).then((arrayBuffer) => {
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      loadSheetData(workbook, sheetName);
      setLoading(false);
    }).catch((error) => {
      console.error('Error loading sheet:', error);
      toast.error('Failed to load sheet');
      setLoading(false);
    });
  };

  // Filter data based on search query
  const filteredData = searchQuery
    ? data.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : data;

  // Paginate the filtered data
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] w-[95vw] h-[90vh] overflow-hidden flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>View Excel File: {fileName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3 flex-1 overflow-auto">
            <Skeleton className="h-10 w-full" />
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* Sheet Selector and Search Bar */}
            <div className="flex items-center gap-4 mb-4">
              {sheets.length > 1 && (
                <div className="w-64">
                  <Select value={selectedSheet} onValueChange={handleSheetChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sheet..." />
                    </SelectTrigger>
                    <SelectContent>
                      {sheets.map((sheet) => (
                        <SelectItem key={sheet} value={sheet}>
                          {sheet}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search in table..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-slate-600 whitespace-nowrap">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of{' '}
                {filteredData.length} rows
                {searchQuery && ` (filtered from ${data.length} total)`}
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {paginatedData.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  {searchQuery ? 'No rows match your search' : 'No data available'}
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-card-bg z-10">
                    <TableRow>
                      {columns.map((column) => (
                        <TableHead key={column} className="font-semibold">
                          {column}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {columns.map((column) => (
                          <TableCell key={column} className="whitespace-nowrap">
                            {row[column] != null ? String(row[column]) : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
